import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { CircularProgress, useMediaQuery } from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';

import Avatar from '@/components/Avatar';
import Badge from '@/components/Badge';
import Card from '@/components/Card';
import Page from '@/components/Page';
import Paginator from '@/components/Paginator/Paginator';
import Stack from '@/components/Stack';
import Table from '@/components/Table';
import { Body, Small } from '@/components/Typography';
import {
  WorkersV2API,
  WorkerListItem,
  WorkersListParams,
  Pagination,
  Skill,
} from '@/services/workersV2API';
import styled from '@/styles';

import WorkerFilterPanel, { FilterState } from './components/WorkerFilterPanel';
import WorkerCard from './components/WorkerCard';
import {
  expandBackgroundFilter,
  expandI9Filter,
  getI9DisplayLabel,
  getDependabilityBadgeStatus,
  getDependabilityLabel,
} from './components/StatusMappings';
import {
  getDisplayedOnboardingPercentage,
  getWorkerBackgroundLabel,
  normalizeWorkerSearch,
} from './helpers';

type SortColumn = 'id' | 'created_at' | 'jobs_worked';

interface SortableHeader {
  label: string;
  sortKey?: SortColumn;
}

const TABLE_HEADERS: SortableHeader[] = [
  { label: 'Worker', sortKey: 'id' },
  { label: 'Onboarding' },
  { label: 'I-9' },
  { label: 'Background' },
  { label: 'Rating' },
  { label: 'Shifts', sortKey: 'jobs_worked' },
  { label: 'Dependability' },
  { label: 'Tax Type' },
  { label: 'Status' },
];

const ProgressBarOuter = styled('div', {
  width: 60,
  height: 6,
  backgroundColor: '$neutralA12',
  borderRadius: '$round',
  overflow: 'hidden',
  display: 'inline-block',
  verticalAlign: 'middle',
  marginLeft: 6,
});

const ProgressBarInner = styled('div', {
  height: '100%',
  borderRadius: '$round',
  variants: {
    color: {
      green: { backgroundColor: '#45A735' },
      yellow: { backgroundColor: '#F5A623' },
      gray: { backgroundColor: '#999999' },
    },
  },
  defaultVariants: {
    color: 'gray',
  },
});

const MobileList = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  padding: '12px',
});

const SortableHeaderCell = styled('th', {
  padding: '12px 8px',
  textAlign: 'left',
  fontSize: '12px',
  fontWeight: 600,
  color: '$neutral11',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  whiteSpace: 'nowrap',
  variants: {
    sortable: {
      true: {
        cursor: 'pointer',
        userSelect: 'none',
        '&:hover': {
          color: '$neutral12',
        },
      },
    },
  },
});

const i9BadgeStatus: Record<string, 'theme' | 'warning' | 'neutral'> = {
  Completed: 'theme',
  Pending: 'warning',
  'In Progress': 'warning',
  'Pending E-Verify': 'warning',
};

const bgBadgeStatus: Record<string, 'theme' | 'danger' | 'warning'> = {
  Approved: 'theme',
  Declined: 'danger',
  Pending: 'warning',
};

const getProgressColor = (pct: number): 'green' | 'yellow' | 'gray' => {
  if (pct >= 100) return 'green';
  if (pct >= 50) return 'yellow';
  return 'gray';
};

const EMPTY_FILTERS: FilterState = {};

const WorkersV2 = () => {
  const navigate = useNavigate();
  const isMobile = useMediaQuery('(max-width: 768px)');
  const [workers, setWorkers] = useState<WorkerListItem[]>([]);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [fetching, setFetching] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Skills state
  const [skills, setSkills] = useState<Skill[]>([]);

  // Search state (separate from filters to avoid double-fetching)
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Filter state (everything except search)
  const [filters, setFilters] = useState<FilterState>(EMPTY_FILTERS);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);

  // Sort state
  const [sortBy, setSortBy] = useState<SortColumn>('created_at');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  const requestSequenceRef = useRef(0);

  // Map skills to the format WorkerFilterPanel expects
  const skillsForPanel = useMemo(
    () => skills.map((s) => ({ id: s.id, name: s.display_name_en })),
    [skills],
  );

  // Load skills on mount
  useEffect(() => {
    const fetchSkills = async () => {
      try {
        const response = await WorkersV2API.getSkills();
        if (response.success) {
          setSkills(response.data);
        }
      } catch (err) {
        console.error('Error fetching skills:', err);
      }
    };
    fetchSkills();
  }, []);

  // Debounce search - only fires after user stops typing for 500ms
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchInput);
      setCurrentPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const fetchWorkers = useCallback(async () => {
    const requestSequence = requestSequenceRef.current + 1;
    requestSequenceRef.current = requestSequence;

    try {
      setFetching(true);
      setError(null);

      const normalizedSearch = normalizeWorkerSearch(debouncedSearch);
      const params: WorkersListParams = {
        page: currentPage,
        per_page: itemsPerPage,
        sort_by: sortBy,
        sort_direction: sortDirection,
      };

      if (normalizedSearch) params.search = normalizedSearch;
      if (filters.onboarding_status?.length)
        params.onboarding_status = filters.onboarding_status;
      if (filters.i9_status?.length)
        params.i9_status = expandI9Filter(filters.i9_status);
      if (filters.background_status?.length)
        params.checkr_status = expandBackgroundFilter(
          filters.background_status,
        );
      if (filters.tax_type) params.tax_type = filters.tax_type as 'w2' | '1099';
      if (filters.active) params.active = filters.active === 'true';
      if (filters.zip) params.zip = filters.zip;
      if (filters.radius) params.radius = parseFloat(filters.radius);
      if (filters.skill_ids?.length) params.skill_ids = filters.skill_ids;
      if (filters.dependability_tier?.length)
        params.dependability_tier = filters.dependability_tier;
      if (filters.accountability_status?.length)
        params.accountability_status = filters.accountability_status;

      const response = await WorkersV2API.getWorkers(params);

      if (requestSequence !== requestSequenceRef.current) {
        return;
      }

      if (response.success) {
        setWorkers(response.data);
        setPagination(response.pagination);
      } else {
        setError('Failed to fetch workers');
      }
    } catch (err) {
      if (requestSequence !== requestSequenceRef.current) {
        return;
      }
      console.error('Error fetching workers:', err);
      setError('Failed to fetch workers');
    } finally {
      if (requestSequence === requestSequenceRef.current) {
        setFetching(false);
        setInitialLoading(false);
      }
    }
  }, [
    currentPage,
    itemsPerPage,
    sortBy,
    sortDirection,
    debouncedSearch,
    filters.accountability_status,
    filters.active,
    filters.background_status,
    filters.dependability_tier,
    filters.i9_status,
    filters.onboarding_status,
    filters.radius,
    filters.skill_ids,
    filters.tax_type,
    filters.zip,
  ]);

  useEffect(() => {
    fetchWorkers();
  }, [fetchWorkers]);

  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  const handleNumberItemsChange = (
    event: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    setCurrentPage(1);
    setItemsPerPage(parseInt(event.target.value));
  };

  const handleWorkerClick = (workerId: number) => {
    navigate(`/workers_v2/${workerId}`);
  };

  const handleFilterChange = (newFilters: FilterState) => {
    setFilters(newFilters);
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    setFilters(EMPTY_FILTERS);
    setSearchInput('');
    setCurrentPage(1);
  };

  const handleSort = (column: SortColumn) => {
    if (sortBy === column) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(column);
      setSortDirection(column === 'created_at' ? 'desc' : 'asc');
    }
    setCurrentPage(1);
  };

  const hasActiveFilters =
    searchInput ||
    filters.onboarding_status?.length ||
    filters.i9_status?.length ||
    filters.background_status?.length ||
    filters.tax_type ||
    filters.active ||
    filters.dependability_tier?.length ||
    filters.accountability_status?.length ||
    filters.skill_ids?.length ||
    filters.zip ||
    filters.radius;

  const renderSortIcon = (column: SortColumn) => {
    if (sortBy !== column) {
      return (
        <UnfoldMoreIcon
          sx={{ fontSize: 14, ml: 0.5, verticalAlign: 'middle', opacity: 0.35 }}
        />
      );
    }
    return sortDirection === 'asc' ? (
      <ArrowUpwardIcon
        sx={{ fontSize: 14, ml: 0.5, verticalAlign: 'middle' }}
      />
    ) : (
      <ArrowDownwardIcon
        sx={{ fontSize: 14, ml: 0.5, verticalAlign: 'middle' }}
      />
    );
  };

  return (
    <Page size="full" title="Workers">
      <Card>
        <Card.Section>
          <WorkerFilterPanel
            filters={filters}
            onFilterChange={handleFilterChange}
            onClear={handleClearFilters}
            skills={skillsForPanel}
            searchValue={searchInput}
            onSearchChange={setSearchInput}
          />
        </Card.Section>

        {initialLoading ? (
          <Card.Section>
            <Stack style={{ justifyContent: 'center', padding: '40px' }}>
              <CircularProgress />
            </Stack>
          </Card.Section>
        ) : error ? (
          <Card.Section>
            <Body color="lighter">{error}</Body>
          </Card.Section>
        ) : workers.length === 0 && !fetching ? (
          <Card.Section>
            <Stack vertical style={{ alignItems: 'center', padding: '40px' }}>
              <Body color="lighter">No workers found</Body>
              {hasActiveFilters && (
                <Small color="lighter">Try adjusting your filters</Small>
              )}
            </Stack>
          </Card.Section>
        ) : isMobile ? (
          <MobileList>
            {workers.map((worker) => (
              <WorkerCard
                key={worker.id}
                worker={worker}
                onClick={() => handleWorkerClick(worker.id)}
              />
            ))}
          </MobileList>
        ) : (
          <div style={{ position: 'relative' }}>
            {fetching && (
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: 'rgba(255, 255, 255, 0.6)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 1,
                }}
              >
                <CircularProgress size={24} />
              </div>
            )}
            <Table>
              <thead>
                <tr>
                  {TABLE_HEADERS.map((header) => (
                    <SortableHeaderCell
                      key={header.label}
                      sortable={!!header.sortKey}
                      onClick={
                        header.sortKey
                          ? () => handleSort(header.sortKey!)
                          : undefined
                      }
                    >
                      {header.label}
                      {header.sortKey && renderSortIcon(header.sortKey)}
                    </SortableHeaderCell>
                  ))}
                </tr>
              </thead>
              <tbody>
                {workers.map((worker) => {
                  const onboardingPercentage =
                    getDisplayedOnboardingPercentage(worker);
                  const i9Label = getI9DisplayLabel(worker.statuses.i9_status);
                  const bgLabel = getWorkerBackgroundLabel(worker);
                  const depLabel = getDependabilityLabel(
                    worker.dependability_tier,
                    worker.accountability_status,
                    worker.is_suspended,
                  );

                  return (
                    <tr key={worker.id}>
                      {/* Worker: Avatar + name + phone + email */}
                      <td style={{ padding: '12px 8px' }}>
                        <Stack gap={10}>
                          <Avatar
                            size="sm"
                            rounded
                            src={worker.avatar_url}
                            firstName={worker.user.first_name}
                            lastName={worker.user.last_name}
                            initialsFormat="firstLast"
                          />
                          <Stack vertical gap={2}>
                            <a
                              href={`/workers_v2/${worker.id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                color: '#1976d2',
                                textDecoration: 'none',
                              }}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <Body weight="semibold">
                                {worker.user.full_name}
                              </Body>
                            </a>
                            <Small
                              color="lighter"
                              css={{ fontSize: '11px', opacity: 0.6 }}
                            >
                              #{worker.id}
                            </Small>
                            {worker.user.phone_number && (
                              <Small color="lighter">
                                {worker.user.phone_number}
                              </Small>
                            )}
                            <Small color="lighter">{worker.user.email}</Small>
                          </Stack>
                        </Stack>
                      </td>

                      {/* Onboarding: % with progress bar */}
                      <td style={{ padding: '12px 8px' }}>
                        <Stack gap={4}>
                          <Small>{onboardingPercentage}%</Small>
                          <ProgressBarOuter>
                            <ProgressBarInner
                              color={getProgressColor(onboardingPercentage)}
                              css={{ width: `${onboardingPercentage}%` }}
                            />
                          </ProgressBarOuter>
                        </Stack>
                      </td>

                      {/* I-9 */}
                      <td style={{ padding: '12px 8px' }}>
                        <Badge
                          label={i9Label}
                          status={i9BadgeStatus[i9Label] || 'neutral'}
                        />
                      </td>

                      {/* Background */}
                      <td style={{ padding: '12px 8px' }}>
                        <Badge
                          label={bgLabel}
                          status={bgBadgeStatus[bgLabel] || 'neutral'}
                        />
                      </td>

                      {/* Rating */}
                      <td style={{ padding: '12px 8px' }}>
                        <Small>
                          {worker.average_rating != null
                            ? `${worker.average_rating.toFixed(1)}`
                            : 'N/A'}
                        </Small>
                      </td>

                      {/* Jobs */}
                      <td style={{ padding: '12px 8px' }}>
                        <Small>{worker.jobs_worked}</Small>
                      </td>

                      {/* Dependability */}
                      <td style={{ padding: '12px 8px' }}>
                        <Badge
                          label={depLabel}
                          status={getDependabilityBadgeStatus(
                            worker.dependability_tier,
                            worker.accountability_status,
                            worker.is_suspended,
                          )}
                        />
                      </td>

                      {/* Tax Type */}
                      <td style={{ padding: '12px 8px' }}>
                        <Badge
                          label={(worker.tax_type || 'N/A').toUpperCase()}
                          status={
                            worker.tax_type === 'w2' ? 'theme' : 'neutral'
                          }
                        />
                      </td>

                      {/* Status */}
                      <td style={{ padding: '12px 8px' }}>
                        <Badge
                          label={worker.active ? 'Enabled' : 'Disabled'}
                          status={worker.active ? 'theme' : 'danger'}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
        )}
      </Card>

      {pagination && workers.length > 0 && (
        <Paginator
          currentPage={pagination.page}
          handleNumberItemsChange={handleNumberItemsChange}
          handlePageChange={handlePageChange}
          itemsLength={pagination.total}
          itemsPerPage={itemsPerPage}
        />
      )}
    </Page>
  );
};

export default WorkersV2;
